import * as Yup from 'yup';
import { Alert, ConfirmationModal } from '@tocktix/pantry';
import { Button, FormControlLabel, FormRadiobuttonGroup, FormTextField, Radio } from 'ui';
import { CSSProperties, ChangeEvent, useEffect, useRef, useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import {
  JceVenueCollectionEntryEntity,
  useSetJceVenueCollectionEntriesForCollectionMutation,
} from 'generated/graphql';
import { getCollectionsForGeographyRefetch } from './queries';
import { gql } from '@apollo/client';
import Papa from 'papaparse';

gql`
  mutation SetJceVenueCollectionEntriesForCollection(
    $collectionId: Int!
    $entries: [JceVenueCollectionEntryInput!]!
  ) {
    setJceVenueCollectionEntriesForCollection(collectionId: $collectionId, entries: $entries) {
      id
      resyVenueId
      tockVenueId
    }
  }
`;

export interface VenueCollectionDetailFormProps {
  geographyId: string;
  collectionId: string | undefined;
  collectionEntrys: readonly JceVenueCollectionEntryEntity[];
}

type VenueEntryInput = {
  resyVenueId?: number;
  tockVenueId?: number;
};

const getVenueEntryLabel = (
  entry: Pick<VenueEntryInput, 'resyVenueId' | 'tockVenueId'>,
  fallback: string
) => {
  if (entry.resyVenueId != null) {
    return `Resy: ${entry.resyVenueId}`;
  }
  if (entry.tockVenueId != null) {
    return `Tock: ${entry.tockVenueId}`;
  }
  return fallback;
};

type VenueIdFormValues = {
  venueType: 'resy' | 'tock';
  venueId: string;
};

type CsvVenueRecord = Record<string, string | undefined>;

const headingStyle: CSSProperties = {
  margin: 0,
  fontSize: '20px',
  fontWeight: 700,
};

const listContainerStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginTop: 12,
  marginBottom: 20,
};

const chipStyle: CSSProperties = {
  padding: '8px 12px',
  backgroundColor: '#e3f2fd',
  borderRadius: '4px',
  fontSize: '14px',
  fontWeight: 500,
  border: '1px solid #90caf9',
  cursor: 'pointer',
};

const venueIdSchema = Yup.object({
  venueType: Yup.string().oneOf(['resy', 'tock']).required('Venue type is required'),
  venueId: Yup.number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .typeError('Venue ID must be a number'),
});

const normalizeCsvHeader = (header: string) => header.trim().toLowerCase();

const getCsvValue = (record: CsvVenueRecord, headerNames: string[]) => {
  const matchedKey = Object.keys(record).find((key) =>
    headerNames.includes(normalizeCsvHeader(key))
  );
  const value = matchedKey ? record[matchedKey] : undefined;
  return value?.trim();
};

const parseVenueId = (value: string | undefined) => {
  if (!value) {
    return undefined;
  }

  const parsed = Number(value);
  if (!Number.isInteger(parsed)) {
    return undefined;
  }

  return parsed;
};

const parseCsvVenueEntries = (
  records: CsvVenueRecord[]
): { entries: VenueEntryInput[]; invalidRowNumbers: number[] } => {
  const entries: VenueEntryInput[] = [];
  const invalidRowNumbers: number[] = [];

  records.forEach((record, index) => {
    const rowNumber = index + 2;
    const venueType = getCsvValue(record, ['venuetype', 'type'])?.toLowerCase();
    const venueIdFromType = parseVenueId(getCsvValue(record, ['venueid', 'id']));
    const resyVenueId = parseVenueId(getCsvValue(record, ['resyvenueid', 'resyid', 'resy']));
    const tockVenueId = parseVenueId(getCsvValue(record, ['tockvenueid', 'tockid', 'tock']));

    if (venueType) {
      if (venueIdFromType === undefined) {
        invalidRowNumbers.push(rowNumber);
        return;
      }

      if (venueType === 'resy') {
        entries.push({ resyVenueId: venueIdFromType });
        return;
      }

      if (venueType === 'tock') {
        entries.push({ tockVenueId: venueIdFromType });
        return;
      }

      invalidRowNumbers.push(rowNumber);
      return;
    }

    if (resyVenueId !== undefined && tockVenueId === undefined) {
      entries.push({ resyVenueId });
      return;
    }

    if (tockVenueId !== undefined && resyVenueId === undefined) {
      entries.push({ tockVenueId });
      return;
    }

    invalidRowNumbers.push(rowNumber);
  });

  return { entries, invalidRowNumbers };
};

const VenueCollectionDetailForm = ({
  geographyId,
  collectionId,
  collectionEntrys,
}: VenueCollectionDetailFormProps) => {
  const [setVenueCollectionEntries] = useSetJceVenueCollectionEntriesForCollectionMutation();
  const [displayedEntries, setDisplayedEntries] = useState<VenueEntryInput[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pendingRemoveEntryIndex, setPendingRemoveEntryIndex] = useState<number | null>(null);
  const csvFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDisplayedEntries(
      collectionEntrys.map((entry) => ({
        resyVenueId: entry.resyVenueId ?? undefined,
        tockVenueId: entry.tockVenueId ?? undefined,
      }))
    );
  }, [collectionEntrys]);

  const applyEntriesUpdate = async (nextEntries: VenueEntryInput[]) => {
    setErrorMessage(null);

    if (!collectionId) {
      setErrorMessage('Unable to update venue IDs: missing collection ID.');
      return false;
    }

    try {
      const response = await setVenueCollectionEntries({
        variables: {
          collectionId: Number(collectionId),
          entries: nextEntries,
        },
        refetchQueries: getCollectionsForGeographyRefetch(Number(geographyId)),
        awaitRefetchQueries: true,
      });

      const serverEntries = response.data?.setJceVenueCollectionEntriesForCollection;
      if (!serverEntries) {
        throw new Error('No venue entries returned from server');
      }

      setDisplayedEntries(
        serverEntries.map((entry) => ({
          resyVenueId: entry.resyVenueId ?? undefined,
          tockVenueId: entry.tockVenueId ?? undefined,
        }))
      );
      setErrorMessage(null);
      return true;
    } catch {
      setErrorMessage('Failed to update venue IDs. Please try again.');
      return false;
    }
  };

  const handleRemoveVenueEntry = async (entryIndex: number) => {
    const entry = displayedEntries[entryIndex];
    if (!entry) {
      return;
    }

    setPendingRemoveEntryIndex(entryIndex);
  };

  const handleConfirmRemoveVenueEntry = async () => {
    if (pendingRemoveEntryIndex === null) {
      return;
    }

    const nextEntries = displayedEntries.filter((_, index) => index !== pendingRemoveEntryIndex);
    await applyEntriesUpdate(nextEntries);
    setPendingRemoveEntryIndex(null);
  };

  const onSubmit = async (
    values: VenueIdFormValues,
    { resetForm }: FormikHelpers<VenueIdFormValues>
  ) => {
    const parsedVenueId = values.venueId === '' ? undefined : Number(values.venueId);
    const nextEntries = [
      ...displayedEntries,
      values.venueType === 'tock' ? { tockVenueId: parsedVenueId } : { resyVenueId: parsedVenueId },
    ].filter((entry) => entry.resyVenueId !== undefined || entry.tockVenueId !== undefined);

    const didUpdate = await applyEntriesUpdate(nextEntries);
    if (didUpdate) {
      resetForm();
    }
  };

  const handleCsvUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setErrorMessage(null);

    Papa.parse<CsvVenueRecord>(file, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data }) => {
        const { entries, invalidRowNumbers } = parseCsvVenueEntries(data);

        if (entries.length === 0) {
          setErrorMessage(
            'No valid entries were found in the CSV. Use headers: venueType + venueId or resyVenueId/tockVenueId.'
          );
          return;
        }

        if (invalidRowNumbers.length > 0) {
          setErrorMessage(
            `Some rows are invalid: ${invalidRowNumbers.join(', ')}. Please fix them and try again.`
          );
          return;
        }

        void applyEntriesUpdate([...displayedEntries, ...entries]);
      },
      error: () => {
        setErrorMessage('Unable to read CSV file. Please try again.');
      },
    });

    if (csvFileInputRef.current) {
      csvFileInputRef.current.value = '';
    }
  };

  return (
    <section style={{ marginTop: 24 }} aria-label="Assigned Venue IDs">
      <h3 style={headingStyle}>Assigned Venue IDs</h3>
      {errorMessage && (
        <div style={{ marginTop: 12, marginBottom: 12 }}>
          <Alert color="danger" title="Failed to update venue IDs" message={errorMessage} />
        </div>
      )}
      {displayedEntries.length > 0 ? (
        <div style={listContainerStyle}>
          {displayedEntries.map((venue, index) => (
            <div
              key={`${venue.resyVenueId ?? 'none'}-${venue.tockVenueId ?? 'none'}`}
              style={chipStyle}
              onClick={() => void handleRemoveVenueEntry(index)}
              title="Click to remove"
            >
              {getVenueEntryLabel(venue, '-')}
            </div>
          ))}
        </div>
      ) : (
        <div style={listContainerStyle}>None</div>
      )}
      <Formik<VenueIdFormValues>
        initialValues={{ venueType: 'resy', venueId: '' }}
        validationSchema={venueIdSchema}
        onSubmit={onSubmit}
      >
        {({ values, dirty, isValid, isSubmitting }) => (
          <Form>
            <FormRadiobuttonGroup name="venueType" label="Venue ID Type" row>
              <FormControlLabel value="resy" control={<Radio />} label="Resy" />
              <FormControlLabel value="tock" control={<Radio />} label="Tock" />
            </FormRadiobuttonGroup>
            <FormTextField
              style={{ marginTop: 12 }}
              label={values.venueType === 'tock' ? 'Tock Venue ID' : 'Resy Venue ID'}
              name="venueId"
            />
            <Button
              color="primary"
              disabled={!dirty || !isValid || isSubmitting}
              size="large"
              type="submit"
              variant="contained"
              style={{ marginTop: 13, marginLeft: 8 }}
            >
              Add Venue ID
            </Button>
            <div style={{ marginTop: 24 }}>
              <input
                ref={csvFileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleCsvUpload}
                style={{ display: 'none' }}
                aria-label="Upload venue IDs CSV"
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={() => csvFileInputRef.current?.click()}
                type="button"
              >
                Upload CSV
              </Button>
              <div style={{ marginTop: 8, fontSize: 12 }}>
                Use CSV headers: <code>venueType (resy or tock), venueId</code> or{' '}
                <code>resyVenueId,tockVenueId (each row must have only one of those columns)</code>.
              </div>
            </div>
          </Form>
        )}
      </Formik>
      <ConfirmationModal
        id="remove-venue-id-confirmation-modal"
        open={pendingRemoveEntryIndex !== null}
        variant="danger"
        title="Remove venue ID?"
        message={`Remove ${getVenueEntryLabel(
          pendingRemoveEntryIndex !== null ? (displayedEntries[pendingRemoveEntryIndex] ?? {}) : {},
          'this venue ID'
        )}?`}
        labels={{ confirm: 'Remove', cancel: 'Cancel' }}
        onCancel={() => setPendingRemoveEntryIndex(null)}
        onClose={() => setPendingRemoveEntryIndex(null)}
        onConfirm={() => {
          void handleConfirmRemoveVenueEntry();
        }}
      />
    </section>
  );
};

export default VenueCollectionDetailForm;
